import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import { getCloudflareUploadUrls } from 'api/apiCloudflare';
import { uploadFileWithProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { runAiJobWithPolling } from 'utils/helpers/ai/runAiJobWithPolling';
import type { ProductImportResultType } from '@econtractor/types';
import type { ImportProductItem, ImportVendorLookup } from './types';

type ImportIaPhase = 'idle' | 'uploading' | 'processing';

interface UseImportIaInfoParams {
    vendors: ImportVendorLookup[];
    onParsed: (items: ImportProductItem[]) => void;
}

const useImportIaInfo = ({ vendors, onParsed }: UseImportIaInfoParams) => {
    const { t } = useTranslation(['Products']);
    const { showToast } = useToast();
    const refInputFile = useRef<HTMLInputElement>(null);
    const [isBusy, setIsBusy] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number | boolean>(false);
    const [phase, setPhase] = useState<ImportIaPhase>('idle');
    const abortControllerRef = useRef<AbortController | null>(null);

    const processingLabel =
        phase === 'uploading' ? t('importIa.loadingUploadFile') : t('importIa.loadingAiProcess');

    const resetInput = () => {
        if (refInputFile.current) {
            refInputFile.current.value = '';
        }
    };

    const handleOpenClick = () => {
        if (isBusy) return;
        refInputFile.current?.click();
    };

    const handleCancel = () => {
        if (phase !== 'processing') return;
        abortControllerRef.current?.abort();
    };

    const handleChangeFile = async () => {
        const file = refInputFile.current?.files?.[0];
        if (!file) return;

        setIsBusy(true);
        setPhase('uploading');
        setUploadProgress(1);

        try {
            const filesMeta = [
                {
                    fileName: file.name,
                    contentType: file.type || 'application/octet-stream',
                    fileSize: file.size,
                },
            ];

            const uploadConfigs = await getCloudflareUploadUrls(filesMeta, {}, { keyPrefix: 'aitemp' });
            const uploadConfig = uploadConfigs?.[0];
            if (!uploadConfig) {
                throw new Error('cloud_upload_urls_missing');
            }

            const uploaded = await uploadFileWithProgress(file, uploadConfig, setUploadProgress);
            if (!uploaded.success || !uploaded.config?.publicUrl) {
                throw new Error('cloud_upload_failed');
            }

            setUploadProgress(100);
            setPhase('processing');
            abortControllerRef.current = new AbortController();
            const job = await runAiJobWithPolling<
                { fileUrl: string; fileName: string; vendors: ImportVendorLookup[] },
                ProductImportResultType
            >({
                requestType: 'products.import.parse-file',
                payload: {
                    fileUrl: uploaded.config.publicUrl,
                    fileName: file.name,
                    vendors,
                },
                pollIntervalMs: 2000,
                timeoutMs: 180000,
                signal: abortControllerRef.current.signal,
            });

            const result = job.result;
            const parsedItems = Array.isArray(result?.items) ? result.items : [];
            onParsed(parsedItems);

            if (parsedItems.length) {
                const skipped = Number(result?.skipped || 0);
                showToast(
                    skipped
                        ? t('importIa.messLoadedWithSkipped', { count: parsedItems.length, skipped })
                        : t('importIa.messLoaded', { count: parsedItems.length }),
                    'success',
                );
            } else {
                showToast(t('importIa.messEmpty'), 'warning');
            }
        } catch (error) {
            const message = (error as Error)?.message || '';
            if (message === 'ai_job_aborted' || message === 'canceled') {
                showToast(t('importIa.messCanceled'), 'warning');
            } else {
                console.error(error);
                showToast(t('importIa.messParseError'), 'danger');
            }
        } finally {
            abortControllerRef.current = null;
            setIsBusy(false);
            setPhase('idle');
            setUploadProgress(false);
            resetInput();
        }
    };

    return {
        refInputFile,
        isBusy,
        uploadProgress,
        phase,
        processingLabel,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    };
};

export default useImportIaInfo;
